import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getSpendBySubcategory } from 'utils/apiRequests';
import {
	ColorSymbol,
	ColorWrapper,
	TooltipGrid,
	TooltipRowTitle,
	TooltipRowValue,
	TooltipWrapper,
} from './DashboardChart.styles';
import { colors } from '@alixpartners/ui-components';
import {
	chartheight,
	FONTSTYLE,
} from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { renderToString } from 'react-dom/server';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { CHART_TYPES, makeUnknownFromEmpty } from 'CategoryManagement/utils';
import { getRequiredCustomFilterObjectFormat } from '../../CategoryManagement/utils';

const SpendBySubCategory = createContext();

export const SpendBySubCategoryProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [options, setOptions] = useState({
		chart: {
			type: 'waterfall',
			spacingTop: 0,
			backgroundColor: 'rgba(0,0,0,0)',
			style: { fontFamily: 'Roboto' },
			marginTop: 20,
			spacingBottom: 20,
			animation: { duration: 200 },
			height: chartheight,
		},
		loading: {
			hideDuration: 500,
			showDuration: 500,
			labelStyle: {
				fontWeight: 'normal',
				letterSpacing: '0.3px',
			},
		},
		title: { text: '' },
		xAxis: {
			reversed: true,
			title: {
				text: ``,
				style: {
					fontSize: '11px',
					fontWeight: '400',
					lineHeight: '13px',
					letterSpacing: 0,
					color: colors.grey7,
				},
			},
			lineColor: colors.black,
			// categories: [],
			labels: {
				formatter: function() {
					if (typeof this.value !== 'number') {
						if (this.value === undefined) {
							return '';
						} else {
							if (this.value.length > 0) {
								return `${this.value.substring(0, 4)}...`;
							}
						}
					}
				},
				style: {
					...FONTSTYLE,
				},
			},
			gridLineWidth: 0,
		},
		yAxis: {
			title: {
				align: 'high',
				text: 'Spend',
				textAlign: 'right',
				rotation: 0,
				y: -10,
				offset: 15,
				style: {
					...FONTSTYLE,
				},
			},
			gridLineWidth: 0,
			labels: {
				formatter: function() {
					return formatSpendWithDecimalAndSymbol(
						this.value,
						currencySymbol,
						hideDecimals,
						0
					).toUpperCase();
				},
				style: {
					...FONTSTYLE,
				},
			},
			lineColor: colors.black,
			lineWidth: 1,
		},
		legend: {
			enabled: false,
			symbolRadius: 1,
			symbolHeight: 16,
			useHTML: true,
			itemMarginBottom: 12,
			labelFormatter: function() {
				return makeUnknownFromEmpty(this.name);
			},
			itemStyle: {
				color: colors.grey8,
				cursor: 'pointer',
				fontSize: '11px',
				fontWeight: 'normal',
				textOverflow: 'ellipsis',
			},
		},
		credits: { enabled: false },
		exporting: { enabled: false },
		// series: [],
		tooltip: {
			enabled: true,
			shared: true,
			distance: 56,
			useHTML: true,
			backgroundColor: 'rgba(255,255,255, 0.9)',
			borderWidth: 0,
			outside: true,
			borderRadius: 2,
		},
		plotOptions: {
			waterfall: {
				stacking: 'normal',
				borderWidth: 0,
				dataLabels: {
					enabled: false,
				},
			},
			column:
				1 === CHART_TYPES.BAR
					? {
							stacking: 1 === CHART_TYPES.STACKED100 ? 'percent' : 'normal',
							dataLabels: {
								enabled: false,
							},
					  }
					: {},
			series: {
				animation: false,
				stacking: 'normal',
				pointPadding: 0,
				groupPadding: 0,
			},
		},
	});

	const {
		currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
		customFilters,
		customFiltersValues,
	} = useContext(CategoryManagementContext);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
						taxonomyType: taxonomyType,
					};
					if (
						Array.isArray(customFiltersValues) &&
						customFiltersValues.length > 0
					) {
						payload.customfilter = getRequiredCustomFilterObjectFormat(
							customFilters,
							customFiltersValues
						);
					}
					getSpendBySubcategory(payload)
						.then(response => {
							if (response.length > 0) {
								setData(response);
								const [xValue, yValue] = Object.keys(response[0]);
								const categoriesResult = getCategories(response, xValue);
								const seriesData = response.map(data => {
									return {
										name: data[xValue],
										y: data[yValue],
									};
								});

								const emptyCol = {
									name: '',
									y: -seriesData[seriesData.length - 1]?.y || 3131232312,
									visible: false,
								};
								seriesData.splice(seriesData.length - 1, 0, emptyCol);

								const seriesResult = [
									{
										upColor: '#032E45',
										data: seriesData,
									},
								];

								setOptions(prevState => ({
									...prevState,
									xAxis: {
										...prevState.xAxis,
										categories: categoriesResult,
									},
									series: seriesResult,
									tooltip: {
										...prevState.tooltip,
										formatter: function() {
											const { points } = this;
											return response.length > 1
												? renderToString(
														<TooltipWrapper>
															<TooltipGrid>
																{points.map(pointData => (
																	<React.Fragment key={pointData.point.name}>
																		<ColorWrapper>
																			<ColorSymbol color={pointData.color} />
																		</ColorWrapper>
																		<TooltipRowTitle>
																			{pointData.point.name}
																		</TooltipRowTitle>
																		<TooltipRowValue>
																			{formatSpendWithDecimalAndSymbol(
																				pointData.y,
																				currencySymbol,
																				hideDecimals,
																				2
																			).toUpperCase()}
																		</TooltipRowValue>
																	</React.Fragment>
																))}
															</TooltipGrid>
														</TooltipWrapper>
												  )
												: renderToString(
														<TooltipWrapper>
															<TooltipGrid>
																<ColorWrapper>
																	<ColorSymbol color={points[0].color} />
																</ColorWrapper>
																<TooltipRowValue>
																	{formatSpendWithDecimalAndSymbol(
																		points[0].y,
																		currencySymbol,
																		hideDecimals,
																		2
																	).toUpperCase()}
																</TooltipRowValue>
															</TooltipGrid>
														</TooltipWrapper>
												  );
										},
									},
								}));
							}
						})
						.catch(() => setIsLoading(false))
						.finally(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[
			dateRangeData,
			categoryName,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			taxonomyType,
			customFilters,
			customFiltersValues,
		]
	);

	const getCategories = (data, key) => {
		const categories = data.map(category => category[key]);
		categories.splice(categories.length - 1, 0, '');
		return categories;
	};

	return (
		<SpendBySubCategory.Provider value={{ isLoading, data, options }}>
			{children}
		</SpendBySubCategory.Provider>
	);
};

export const useSpendBySubCategoryContext = () => {
	return useContext(SpendBySubCategory);
};
